import { useState } from 'react';

import { BottomSheet } from '~/modules/elements/akui/bottom-sheet';
import styles from '~/modules/game-engine/drawing/styles';
import { serverRpc } from '~/modules/remote-mic/network/client';
import { useServerMutation } from '~/modules/remote-mic/network/client/hooks/use-server-mutation';
import { useServerQuery } from '~/modules/remote-mic/network/client/hooks/use-server-query';
import { useSubscription } from '~/modules/remote-mic/network/client/hooks/use-subscription';
import { InfoButton, InfoLine } from '~/routes/remote-mic/components/info-reveal';
import NumericInput from '~/routes/remote-mic/components/numeric-input';
import useRemoteMicId from '~/routes/remote-mic/hooks/use-remote-mic-id';
import { CheckBox } from '~/routes/remote-mic/panels/microphone/remote-controls';
import ManagePlayerPanel from '~/routes/remote-mic/panels/remote-settings/manage-player-panel';

interface Props {
  open: boolean;
  onClose: () => void;
}

export default function ManageGameSheet({ open, onClose }: Props) {
  const mics = useSubscription('remote-mics') ?? [];
  const myId = useRemoteMicId();
  const [managing, setManaging] = useState<string | null>(null);
  const managedMic = mics.find((mic) => mic.id === managing);

  const {
    data: inputLag = 0,
    loading: lagLoading,
    refetch: refetchLag,
  } = useServerQuery(() => serverRpc.settings.getInputLag());
  const { mutate: setLag, loading: settingLag } = useServerMutation(async (newValue: number) => {
    await serverRpc.settings.setInputLag(newValue);
    refetchLag();
  });

  const {
    data: unassign = false,
    loading: unassignLoading,
    refetch: refetchUnassign,
  } = useServerQuery(() => serverRpc.settings.getUnassignAfterSong());
  const { mutate: setUnassign, loading: settingUnassign } = useServerMutation(async (newValue: boolean) => {
    await serverRpc.settings.setUnassignAfterSong(newValue);
    refetchUnassign();
  });

  return (
    <>
      <BottomSheet open={open && managedMic === undefined} onClose={onClose} title="Manage the game">
        <div className="flex flex-col gap-3">
          <span className="text-[0.8125rem] tracking-[0.08em] text-white/50 uppercase">Connected microphones</span>
          <div className="flex flex-col overflow-hidden rounded-[1.125rem] bg-white/6 shadow-[inset_0_0_0_1px_rgba(255,255,255,0.12)] [&>*:not(:last-child)]:border-b [&>*:not(:last-child)]:border-white/8">
            {mics.map((mic) => (
              <button
                key={mic.id}
                type="button"
                onClick={() => setManaging(mic.id)}
                data-test="remote-mic-entry"
                data-id={mic.id ?? mic.name}
                className="flex h-15 items-center gap-3 border-none bg-transparent px-4.5 text-left text-white transition-colors duration-150 active:bg-white/6">
                <span
                  aria-hidden
                  className="size-5 shrink-0 rounded-full"
                  style={{
                    background:
                      mic.number !== null ? styles.colors.players[mic.number].perfect.fill : 'rgba(255,255,255,.18)',
                  }}
                />
                <span className="shrink-0 font-mono text-[0.875rem] text-white/50">{mic.id?.slice(-4) ?? '????'}</span>
                <span
                  className={`ph-no-capture min-w-0 flex-1 truncate text-[1.0625rem] ${mic.id === myId ? 'text-active' : 'text-white'}`}>
                  {mic.name}
                </span>
              </button>
            ))}
            {mics.length === 0 && (
              <div className="flex h-15 items-center px-4.5 text-[0.9375rem] text-white/60">
                No remote microphones connected
              </div>
            )}
          </div>

          <span className="flex items-center gap-2 text-[0.8125rem] tracking-[0.08em] text-white/50 uppercase">
            Game input lag
            <InfoButton infoKey="GameInputLag" />
          </span>
          <NumericInput
            value={inputLag}
            onChange={(value) => void setLag(value)}
            disabled={lagLoading || settingLag}
            unit="ms"
            surface="sheet"
            data-test="game-input-lag"
          />
          <InfoLine infoKey="GameInputLag">
            If the sound is not synchronised with the lyrics, use this to compensate it.
          </InfoLine>

          <button
            type="button"
            onClick={() => void setUnassign(!unassign)}
            disabled={unassignLoading || settingUnassign}
            data-test="reset-players"
            data-checked={unassign}
            className="flex h-16 cursor-pointer items-center gap-3 rounded-[1.125rem] border-none bg-white/8 px-4.5 text-left text-white shadow-[inset_0_0_0_1px_rgba(255,255,255,0.14)] transition-colors duration-150 active:bg-white/14 disabled:opacity-40">
            <CheckBox checked={unassign} />
            <span className="min-w-0 flex-1 truncate text-[1.0625rem]">Reset players after each song</span>
            <InfoButton infoKey="ResetPlayers" />
          </button>
          <InfoLine infoKey="ResetPlayers">
            Unassigns everyone once a song ends, so nobody holds a colour they stopped singing with.
          </InfoLine>
        </div>
      </BottomSheet>

      <BottomSheet
        open={managedMic !== undefined}
        onClose={() => setManaging(null)}
        onBack={() => setManaging(null)}
        title={managedMic ? `Change ${managedMic.name}` : ''}>
        {managedMic && (
          <ManagePlayerPanel
            id={managedMic.id}
            name={managedMic.name}
            playerNumber={managedMic.number}
            onModalClose={() => setManaging(null)}
          />
        )}
      </BottomSheet>
    </>
  );
}
